import type { PublicRoadTraffic, PublicSnapshot } from "../shared/contracts";

const BRICKELL_DIRECTIONS = [
  ["brickell-bridge-east-carriageway", "NB"],
  ["brickell-bridge-west-carriageway", "SB"],
] as const;

const conditionLabel: Record<Exclude<PublicRoadTraffic["condition"], "unavailable">, string> = {
  free_flow: "free flow",
  moderate: "moderate",
  heavy: "heavy",
  severe: "severe",
};

export interface ModeledRoadTrafficSignal {
  jamFactor: number;
  densityProxy: number;
  speedMph: number;
  freeFlowSpeedMph: number;
}

export interface RoadTrafficRenderSignal {
  jamFactor: number;
  densityProxy: number;
  speedMph: number | null;
  freeFlowSpeedMph: number | null;
}

export interface RoadTrafficPathRenderSignal extends RoadTrafficRenderSignal {
  pathId: string;
}

export interface RoadTrafficRenderSignals {
  source: "tomtom" | "modeled";
  aggregate: RoadTrafficRenderSignal;
  roads: RoadTrafficPathRenderSignal[];
}

const MINIMUM_LIVE_DENSITY_PROXY = 0.2;
const MAXIMUM_DENSITY_PROXY = 0.98;

/**
 * Provider attribution requires three independent truths: a live product snapshot,
 * an explicit TomTom source, and usable current observations. Demo/model values can
 * still animate the synthetic fleet, but can never inherit provider wording.
 */
export function usesTomTomRoadTraffic(
  dataMode: PublicSnapshot["dataMode"],
  traffic: PublicRoadTraffic,
): boolean {
  return dataMode === "live" && hasCurrentTomTomRoadTraffic(traffic);
}

/**
 * Converts aggregate flow loss into a visual occupancy proxy. TomTom does not
 * provide observed vehicle positions or a literal count here, so the scene
 * combines this bounded lift with a time-of-day baseline and keeps speed as a
 * separate input.
 */
export function congestionPercentToDensityProxy(congestionPercent: number): number {
  const congestion = clamp(congestionPercent, 0, 100) / 100;
  return MINIMUM_LIVE_DENSITY_PROXY
    + (MAXIMUM_DENSITY_PROXY - MINIMUM_LIVE_DENSITY_PROXY) * congestion ** 0.72;
}

/**
 * Produces renderer inputs from current per-path flow evidence. A free-flow
 * street keeps the modeled occupancy floor instead of becoming empty; local
 * slowdowns can only lift its population. Stale, unavailable, and modeled
 * payloads return the supplied time-of-day model without provider semantics.
 */
export function roadTrafficRenderSignals(
  traffic: PublicRoadTraffic,
  modeled: ModeledRoadTrafficSignal,
): RoadTrafficRenderSignals {
  const fallback: RoadTrafficRenderSignal = {
    ...modeled,
    densityProxy: clamp(modeled.densityProxy, 0.04, MAXIMUM_DENSITY_PROXY),
  };
  if (!hasCurrentTomTomRoadTraffic(traffic) || traffic.congestionPercent === null) {
    return { source: "modeled", aggregate: fallback, roads: [] };
  }

  const aggregateDensity = Math.max(
    fallback.densityProxy,
    congestionPercentToDensityProxy(traffic.congestionPercent),
  );
  // Individual flow features describe relative speed, not the absence of
  // traffic. Preserve most of the aggregate/time baseline on free-flow paths.
  const pathDensityFloor = Math.max(
    fallback.densityProxy * 0.9,
    aggregateDensity * 0.78,
  );

  return {
    source: "tomtom",
    aggregate: {
      jamFactor: traffic.congestionPercent / 10,
      densityProxy: aggregateDensity,
      speedMph: traffic.speedMph,
      freeFlowSpeedMph: traffic.freeFlowSpeedMph,
    },
    roads: traffic.roads.map((road) => ({
      pathId: road.pathId,
      jamFactor: road.congestionPercent / 10,
      densityProxy: Math.max(
        pathDensityFloor,
        congestionPercentToDensityProxy(road.congestionPercent),
      ),
      speedMph: road.speedMph,
      freeFlowSpeedMph: road.freeFlowSpeedMph,
    })),
  };
}

/** Compact, decision-first state for the Brickell Avenue crossing card. */
export function brickellAvenueTrafficLabel(traffic: PublicRoadTraffic): string {
  const current = traffic.source === "tomtom" &&
    (traffic.freshness === "live" || traffic.freshness === "delayed");
  if (!current) return "Brickell Ave street traffic · Live data unavailable";

  const directions = BRICKELL_DIRECTIONS.flatMap(([pathId, direction]) => {
    const road = traffic.roads.find((candidate) => candidate.pathId === pathId);
    if (!road) return [];
    const speed = road.speedMph === null ? "" : ` at ${Math.round(road.speedMph)} mph`;
    return [`${direction} ${conditionLabel[road.condition]}${speed}`];
  });
  return directions.length === 0
    ? "Brickell Ave street traffic · Live data unavailable"
    : `Brickell Ave street traffic · ${directions.join(" · ")}`;
}

function hasCurrentTomTomRoadTraffic(traffic: PublicRoadTraffic): boolean {
  return traffic.source === "tomtom" &&
    traffic.condition !== "unavailable" &&
    traffic.roads.length > 0 &&
    (traffic.freshness === "live" || traffic.freshness === "delayed");
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
